import React, { useState } from "react";
import { useAutoPoll } from "./useAutoPoll";
import { backendUrl } from "@/lib/seoApi";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { RefreshChip, LoadingState, ErrorState } from "./OpsDashboard";

const PERIODS = [
  { value: "today", label: "Today" },
  { value: "week",  label: "Last 7 days" },
  { value: "month", label: "Last 30 days" },
  { value: "all",   label: "All time" },
];

export default function OpsCosts() {
  const [period, setPeriod] = useState("today");
  const { data, loading, error, lastUpdated } = useAutoPoll(
    backendUrl(`/api/seo/ops/costs?period=${period}&limit=100`),
    { intervalMs: 15000 });

  if (error) return <ErrorState message={error} />;
  if (!data)  return <LoadingState />;

  const summary = data.summary || { total_usd: 0, calls: 0, rows: [] };
  const rows = summary.rows || [];
  const ledger = data.ledger || [];
  const maxCost = Math.max(...rows.map(r => r.cost_usd), 0.0001);

  return (
    <div className="p-6" data-testid="ops-costs">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-semibold text-neutral-900">Cost Ledger</h1>
          <p className="text-sm text-neutral-500 mt-1">Unified spend across DataForSEO, Text.ru, LLM.</p>
        </div>
        <div className="flex items-center gap-3">
          <select value={period} onChange={(e) => setPeriod(e.target.value)}
                    data-testid="cost-period-select"
                    className="h-9 border border-neutral-200 rounded-md px-2 text-sm bg-white">
            {PERIODS.map(p => <option key={p.value} value={p.value}>{p.label}</option>)}
          </select>
          <RefreshChip lastUpdated={lastUpdated} loading={loading} />
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-6">
        <div className="border rounded-lg p-4" data-testid="cost-total">
          <div className="text-xs text-neutral-500 uppercase">Total spend</div>
          <div className="mt-2 text-3xl font-semibold tabular-nums">${summary.total_usd?.toFixed(4)}</div>
        </div>
        <div className="border rounded-lg p-4">
          <div className="text-xs text-neutral-500 uppercase">API calls</div>
          <div className="mt-2 text-3xl font-semibold tabular-nums">{summary.calls}</div>
        </div>
        <div className="border rounded-lg p-4">
          <div className="text-xs text-neutral-500 uppercase">Services</div>
          <div className="mt-2 text-3xl font-semibold tabular-nums">{new Set(rows.map(r => r.service)).size}</div>
        </div>
      </div>

      <Card className="mb-6">
        <CardHeader><CardTitle className="text-base">By service · operation</CardTitle></CardHeader>
        <CardContent>
          {rows.length === 0 && <div className="text-sm text-neutral-500">No spend recorded in this period.</div>}
          {rows.length > 0 && (
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs text-neutral-500 uppercase tracking-wide">
                  <th className="pb-2">Service</th><th>Operation</th><th className="text-right">Calls</th>
                  <th className="text-right">Cache hits</th><th className="text-right">Cost</th><th></th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r, i) => (
                  <tr key={i} className="border-t border-neutral-100" data-testid={`cost-row-${i}`}>
                    <td className="py-2 font-medium">{r.service}</td>
                    <td className="text-neutral-600">{r.operation}</td>
                    <td className="text-right tabular-nums">{r.calls}</td>
                    <td className="text-right tabular-nums text-neutral-500">{r.cache_hits}</td>
                    <td className="text-right tabular-nums font-semibold">${r.cost_usd.toFixed(4)}</td>
                    <td className="pl-3 w-32">
                      <div className="h-2 bg-neutral-100 rounded-full overflow-hidden">
                        <div className="h-full bg-neutral-800" style={{ width: `${100 * r.cost_usd / maxCost}%` }} />
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader><CardTitle className="text-base">Recent transactions</CardTitle></CardHeader>
        <CardContent>
          {ledger.length === 0 && <div className="text-sm text-neutral-500">No transactions.</div>}
          {ledger.length > 0 && (
            <table className="w-full text-xs">
              <thead>
                <tr className="text-left text-neutral-500 uppercase tracking-wide">
                  <th className="pb-2">Time</th><th>Service</th><th>Operation</th>
                  <th>Source</th><th className="text-right">Cost</th>
                </tr>
              </thead>
              <tbody>
                {ledger.map((r, i) => (
                  <tr key={i} className="border-t border-neutral-100">
                    <td className="py-1.5 text-neutral-500 whitespace-nowrap">{r.at?.slice(0, 19).replace("T", " ")}</td>
                    <td className="font-medium">{r.service}</td>
                    <td className="text-neutral-700">{r.operation}</td>
                    <td>
                      <span className={`text-[10px] px-1.5 py-0.5 rounded-full ${r.cache ? "bg-emerald-50 text-emerald-700" : "bg-neutral-100 text-neutral-600"}`}>
                        {r.cache ? "cache" : r.source || "api"}
                      </span>
                    </td>
                    <td className="text-right tabular-nums">${r.cost_usd.toFixed(4)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
